import React, { useState, useEffect, useCallback } from 'react';
import {
  Leaf, Sprout, FlaskConical, Activity, Check, X, Minus, Plus,
  ShieldCheck, Loader2, Waves, Timer, Gauge, Droplets, ArrowRight, Beaker
} from 'lucide-react';
import Header from './Header';
import Footer from './Footer';
import CartDrawer from './CartDrawer';
import {
  Accordion, AccordionItem, AccordionTrigger, AccordionContent
} from './ui/accordion';
import productService from '../services/productService';
import cartService from '../services/cartService';

const PRODUCT_HANDLE = 'daily-multi-fiber';

// Shopify CDN product images (auto-replaced by live Storefront images once published)
const FALLBACK_IMAGES = [
  'https://www.mdlogichealth.com/cdn/shop/files/daily_multi_fiber_1.png?v=1781629077&width=1000',
  'https://www.mdlogichealth.com/cdn/shop/files/daily_multi_fiber_2.png?v=1781648008&width=1000',
  'https://www.mdlogichealth.com/cdn/shop/files/daily_multi_fiber.png?v=1781726612&width=1000'
];
const FALLBACK_PRICE = '44.95';
// Live variant (SKU MDL133-450GMS) — resilient fallback so Add-to-Cart works
// even if the live product fetch is momentarily unavailable.
const FALLBACK_VARIANT_ID = 'gid://shopify/ProductVariant/48768342655232';

const LIZ_IMG = 'https://customer-assets-0z36b82j.emergentagent.net/job_4614a4a9-8736-46ee-9333-eb2268a85e9b/artifacts/i9wz46it_IMG_1916.jpeg';
const LIZ_NOTE = [
  `If you’re wondering why a gal who told folks for years that "you can get everything you need from food" would take a fiber supplement...`,
  `It's simple…I’m talking about closing a gap as conveniently as possible with a solution we know is helpful. And, for a lot of us, fiber IS the gap.`,
  `I like Daily Multi-Fiber because it’s got FOUR fibers, not one, all of which have unique properties* that I've been researching for several years…`,
  `If you've been through my metabolism programs, you've heard me go on about this: resistant starch feeds, nourishes, and fuels your healthy gut bacteria, which has a powerful say in the health of your gut, and your metabolism downstream of that.`,
  `This ain’t your granddad's Benefiber. It's a truly thoughtful formula that earns its spot for us 40+ fiber-needers. Plus, it’s tasteless, so it’s easy to take!`
];

const STATS = [
  { value: '10g', label: 'Fiber / scoop' },
  { value: '4', label: 'Plant fibers' },
  { value: '30', label: 'Servings' },
  { value: '25', label: 'Calories' }
];

const FIBERS = [
  {
    num: '01', tag: 'Soluble · Prebiotic', title: 'Organic Acacia Gum', source: 'From the acacia tree',
    icon: Leaf,
    body: 'A gentle, non-viscous soluble fiber that mixes invisibly and is well tolerated even by sensitive stomachs. Long valued for nourishing beneficial gut bacteria.*'
  },
  {
    num: '02', tag: 'Resistant Starch · RS2', title: 'Solnul® Potato Starch', source: 'Upcycled non-GMO potato',
    icon: Sprout,
    body: 'A clinically studied resistant potato starch that travels intact like an insoluble fiber, then ferments fully like a soluble one to feed the gut.*'
  },
  {
    num: '03', tag: 'Soluble · Short-Chain', title: 'scFOS', source: 'From sugar beet',
    icon: Droplets,
    body: 'Short-chain fructooligosaccharides ferment quickly and early in the colon, an easy fuel source for fast-acting beneficial bacteria.*'
  },
  {
    num: '04', tag: 'Soluble · Long-Chain', title: 'Inulin', source: 'From chicory root',
    icon: Waves,
    body: 'A longer-chain fructan that ferments more slowly and deeper in the colon, extending prebiotic activity further down the digestive tract.*'
  }
];

const RELAY = [
  {
    icon: Timer, kicker: 'Rapid & selective', title: 'Short-chain FOS gets there first',
    body: 'From sugar beet, scFOS ferments quickly and early in the colon, a fast fuel that selectively feeds Bifidobacterium and butyrate-producing Anaerostipes.*'
  },
  {
    icon: Gauge, kicker: 'Highly fermentable', title: 'Inulin keeps the momentum',
    body: 'From chicory root, inulin is rapidly used by beneficial microbes to drive bifidogenesis and the production of short-chain fatty acids like acetate.*'
  },
  {
    icon: Activity, kicker: 'Slow & steady', title: 'Acacia works the long stretch',
    body: 'A gentle, well-tolerated exudate from the acacia tree, it ferments slowly and steadily further along, supporting Bifidobacterium and Lactobacillus and the colon\u2019s protective barrier.*'
  },
  {
    icon: Sprout, kicker: 'Extremely slow · FODMAP-friendly', title: 'Solnul® reaches the deep end',
    body: 'The resistant potato starch travels intact like an insoluble fiber, then ferments completely like a soluble one, reaching the far colon and cross-feeding butyrate-producers that help enrich Akkermansia.*'
  }
];

const FEELINGS = [
  { icon: Activity, title: 'Regularity & Motility', body: 'Promotes optimal gut motility and natural digestive regularity, so your routine stays comfortable and predictable.*' },
  { icon: Leaf, title: 'Nourished Microflora', body: 'Helps fuel and nourish the beneficial gut microflora that underpin a balanced, resilient digestive system.*' },
  { icon: ShieldCheck, title: 'Prolonged Satiety', body: 'Supports a feeling of prolonged satiety, helping you feel comfortably full between meals.*' }
];

const COMPARE = [
  { label: 'Fiber sources', a: '4 complementary fibers', b: 'Usually 1 source', c: 'Often 1 source' },
  { label: 'Fiber per serving', a: '10 g', b: '~2.4\u20137 g', c: '~3\u20135 g' },
  { label: 'Clinically studied key ingredient', a: 'Solnul® RPS', b: 'Rarely', c: 'Rarely' },
  { label: 'Prebiotic: feeds good bacteria', a: 'Yes', b: 'Varies by fiber', c: 'Often' },
  { label: 'No added sugar or sweeteners', a: true, b: 'Varies', c: 'Often added' },
  { label: 'No artificial flavors or colors', a: true, b: 'Often flavored', c: 'Often added' },
  { label: 'Unflavored: mixes into anything', a: 'Yes', b: 'Usually flavored', c: 'Often added' }
];

const FREE_FROM = [
  'No milk or soy', 'No egg or fish', 'No shellfish', 'No tree nuts or peanuts',
  'No wheat or sesame', 'No artificial colors', 'No artificial flavors',
  'No stearates or palmitates', 'No phthalates', 'No toxic fillers'
];

const FAQS = [
  { q: 'Does it really have no taste?', a: 'Daily Multi-Fiber is completely unflavored and additive-free. It dissolves cleanly into water, juice, smoothies, or your morning coffee without changing the flavor. Just give it a quick blend or shake.' },
  { q: 'When and how should I take it?', a: 'Blend one rounded scoop into 6\u20138 oz of your beverage once daily, ideally about 15 minutes before your first meal, or take it as directed by your health care professional.' },
  { q: 'What makes a 4-fiber blend better than a single fiber?', a: 'Different fibers ferment at different speeds and in different regions of the colon. Combining short-chain scFOS, longer-chain inulin, soluble acacia, and resistant Solnul® potato starch provides prebiotic activity across the length of the gut rather than in one spot.*' },
  { q: 'Is it gentle on a sensitive stomach?', a: 'The blend is built around well-tolerated fibers, and Solnul® is low-FODMAP at its studied dose and ferments slowly. As with any fiber, you can start with a smaller amount and build up. If you\u2019re pregnant, lactating, or being treated for a medical condition, talk with your health care professional first.' },
  { q: 'Who formulates MD Logic Health® products?', a: 'Our formulas are developed by our expert team of professional formulators, which includes our pharmacist, Shanti Ramjeet, RPH, doctors, scientists and nutritionists. Every product is made in the USA in a GMP-certified facility and rigorously lab-tested for contaminants.' },
  { q: 'Is it vegan and allergen-friendly?', a: 'Yes! It\u2019s plant-based and contains no milk, soy, egg, fish, shellfish, tree nuts, wheat, peanuts, soybean, or sesame, and no gluten, artificial colors, or flavors.' }
];

const HOW_TO = [
  'Mix a single rounded scoop into 6\u20138 oz of your favorite beverage or water.',
  'Blend or shake. It is unflavored, so it disappears into whatever you\u2019re drinking.',
  'Take with your first meal, daily, or as directed by your health care professional.'
];

const CompareCell = ({ value, strong }) => {
  if (value === true) {
    return <Check className={`w-4 h-4 mx-auto ${strong ? 'text-emerald-600' : 'text-stone-400'}`} />;
  }
  return <span className={strong ? 'font-semibold text-emerald-800' : 'text-stone-500'}>{value}</span>;
};

const BuyBox = ({ price, image, quantity, setQuantity, onAdd, adding, available, variant = 'light', centered = false }) => {
  const dark = variant === 'dark';
  return (
    <div
      data-testid="fiber-buy-box"
      className={`rounded-2xl p-6 md:p-8 ${centered ? 'text-center' : ''} ${dark ? 'bg-white/5 border border-white/10 backdrop-blur-sm' : 'bg-white border border-stone-200 shadow-xl shadow-stone-200/50'}`}
    >
      <div className={`flex items-center gap-1 mb-3 ${centered ? 'justify-center' : ''}`}>
        <span className={`text-xs ${dark ? 'text-white/60' : 'text-stone-500'}`}>Doctor-formulated, Pharmacist-verified</span>
      </div>
      <div className={`flex items-end gap-3 mb-1 ${centered ? 'justify-center' : ''}`}>
        <span className={`text-4xl font-semibold tracking-tight ${dark ? 'text-white' : 'text-stone-900'}`} data-testid="fiber-price">
          ${price}
        </span>
        <span className={`text-sm mb-1 ${dark ? 'text-white/60' : 'text-stone-500'}`}>/ 450g · 30 servings</span>
      </div>
      <p className={`text-sm mb-6 ${dark ? 'text-white/70' : 'text-stone-600'}`}>
        Unflavored & additive-free. Mixes clean into any beverage. Take it with your first meal.
      </p>

      <div className={`flex items-center gap-4 mb-4 ${centered ? 'justify-center' : ''}`}>
        <span className={`text-xs uppercase tracking-wider ${dark ? 'text-white/60' : 'text-stone-500'}`}>Quantity</span>
        <div className={`flex items-center rounded-full border ${dark ? 'border-white/20' : 'border-stone-300'}`}>
          <button
            onClick={() => setQuantity(Math.max(1, quantity - 1))}
            data-testid="fiber-qty-decrease"
            className={`p-2.5 rounded-l-full transition-colors ${dark ? 'text-white hover:bg-white/10' : 'text-stone-700 hover:bg-stone-100'}`}
          >
            <Minus className="w-4 h-4" />
          </button>
          <span className={`w-10 text-center text-sm font-medium ${dark ? 'text-white' : 'text-stone-900'}`} data-testid="fiber-qty-value">
            {quantity}
          </span>
          <button
            onClick={() => setQuantity(quantity + 1)}
            data-testid="fiber-qty-increase"
            className={`p-2.5 rounded-r-full transition-colors ${dark ? 'text-white hover:bg-white/10' : 'text-stone-700 hover:bg-stone-100'}`}
          >
            <Plus className="w-4 h-4" />
          </button>
        </div>
      </div>

      <button
        onClick={onAdd}
        disabled={adding}
        data-testid="fiber-add-to-cart-btn"
        className="w-full flex items-center justify-center gap-2 py-4 rounded-full bg-emerald-700 text-white font-medium hover:bg-emerald-800 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {adding ? (
          <><Loader2 className="w-4 h-4 animate-spin" /> Adding&hellip;</>
        ) : (
          <>Add to Cart <ArrowRight className="w-4 h-4" /></>
        )}
      </button>

      {!available && (
        <p className={`text-[11px] mt-3 text-center ${dark ? 'text-amber-200/80' : 'text-amber-700'}`} data-testid="fiber-unavailable-note">
          Live inventory syncs once this product is published to the store.
        </p>
      )}

      <div className={`flex flex-wrap items-center justify-center gap-x-4 gap-y-2 mt-6 text-[11px] ${dark ? 'text-white/60' : 'text-stone-500'}`}>
        {['Made in the USA', 'GMP Certified', 'Gluten-Free', 'BPA-Free', 'Lab-Tested'].map((b) => (
          <span key={b} className="flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> {b}
          </span>
        ))}
      </div>
    </div>
  );
};

const DailyMultiFiber = () => {
  const [product, setProduct] = useState(null);
  const [activeImg, setActiveImg] = useState(0);
  const [quantity, setQuantity] = useState(1);

  const [cart, setCart] = useState(null);
  const [cartOpen, setCartOpen] = useState(false);
  const [cartLoading, setCartLoading] = useState(false);
  const [adding, setAdding] = useState(false);

  // Try to pull live product data; fall back to scraped Shopify CDN content
  useEffect(() => {
    const load = async () => {
      try {
        const p = await productService.getProductByHandle(PRODUCT_HANDLE);
        if (p && p.variantId) setProduct(p);
      } catch (err) {
        // Not published to the Storefront channel yet — use fallback content
        console.warn('Daily Multi-Fiber not live yet, using fallback content');
      }
    };
    load();
  }, []);

  useEffect(() => {
    const loadCart = async () => {
      try {
        const existing = await cartService.getCart();
        if (existing) setCart(existing);
      } catch (err) {
        console.error('Failed to load cart', err);
      }
    };
    loadCart();
  }, []);

  const images = (product?.images && product.images.length ? product.images : FALLBACK_IMAGES);
  const price = product ? parseFloat(product.price || FALLBACK_PRICE).toFixed(2) : FALLBACK_PRICE;
  const variantId = product?.variantId || FALLBACK_VARIANT_ID;
  const available = true;

  const handleAddToCart = useCallback(async () => {
    setAdding(true);
    try {
      const updated = await cartService.addToCart(variantId, quantity);
      setCart(updated);
      setCartOpen(true);
    } catch (err) {
      console.error('Failed to add to cart', err);
    } finally {
      setAdding(false);
    }
  }, [variantId, quantity]);

  const handleUpdateQuantity = useCallback(async (lineId, qty) => {
    setCartLoading(true);
    try {
      const updated = qty === 0
        ? await cartService.removeFromCart([lineId])
        : await cartService.updateCartLine(lineId, qty);
      setCart(updated);
    } catch (err) {
      console.error('Failed to update quantity', err);
    } finally {
      setCartLoading(false);
    }
  }, []);

  const handleRemoveItem = useCallback(async (lineId) => {
    setCartLoading(true);
    try {
      const updated = await cartService.removeFromCart([lineId]);
      setCart(updated);
    } catch (err) {
      console.error('Failed to remove item', err);
    } finally {
      setCartLoading(false);
    }
  }, []);

  const handleCheckout = useCallback(async () => {
    setCartLoading(true);
    try {
      await cartService.checkout();
    } catch (err) {
      console.error('Failed to checkout', err);
    } finally {
      setCartLoading(false);
    }
  }, []);

  const cartCount = cart?.lines?.reduce((sum, l) => sum + l.quantity, 0) || 0;

  return (
    <div className="min-h-screen bg-white" data-testid="daily-multi-fiber-page">
      <Header cartCount={cartCount} onCartClick={() => setCartOpen(true)} />

      <main>
        {/* Hero */}
        <section className="relative overflow-hidden bg-gradient-to-b from-[#12261d] via-[#173026] to-[#1d3a2c] text-white">
          <div className="absolute -top-24 -left-24 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl" />
          <div className="absolute -bottom-24 -right-24 w-96 h-96 bg-lime-400/10 rounded-full blur-3xl" />
          <div className="relative max-w-7xl mx-auto px-6 pt-32 md:pt-40 pb-16 md:pb-24 grid lg:grid-cols-2 gap-12 items-center">
            {/* Left — copy + buy box */}
            <div>
              <span className="inline-block text-[11px] uppercase tracking-[0.25em] text-lime-200/90 mb-5">
                Premium 4-Fiber Blend · Unflavored
              </span>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif leading-tight mb-5">
                Daily Multi-Fiber
              </h1>
              <p className="text-base md:text-lg text-white/75 max-w-xl leading-relaxed mb-8">
                Four plant-based fibers in one effortless scoop, featuring clinically studied
                Solnul® resistant potato starch to feed your gut and keep things moving.*
              </p>

              <div className="grid grid-cols-4 gap-3 mb-10 max-w-lg">
                {STATS.map((s) => (
                  <div key={s.label} className="text-center rounded-xl bg-white/5 border border-white/10 py-3 px-1">
                    <div className="text-2xl font-semibold text-lime-200">{s.value}</div>
                    <div className="text-[10px] uppercase tracking-wider text-white/50 mt-1 leading-tight">{s.label}</div>
                  </div>
                ))}
              </div>

              <div className="max-w-md">
                <BuyBox
                  price={price} image={images[0]} quantity={quantity} setQuantity={setQuantity}
                  onAdd={handleAddToCart} adding={adding} available={available} variant="dark"
                />
              </div>
            </div>

            {/* Right — image gallery */}
            <div className="order-first lg:order-last">
              <div className="rounded-3xl bg-white/5 border border-white/10 p-6 md:p-10">
                <div className="aspect-square rounded-2xl bg-white/95 overflow-hidden flex items-center justify-center">
                  <img
                    src={images[activeImg]}
                    alt="Daily Multi-Fiber"
                    className="w-full h-full object-contain p-6"
                    data-testid="fiber-hero-image"
                  />
                </div>
                <div className="flex items-center justify-center gap-3 mt-5">
                  {images.slice(0, 4).map((img, i) => (
                    <button
                      key={i}
                      onClick={() => setActiveImg(i)}
                      data-testid={`fiber-thumb-${i}`}
                      className={`w-16 h-16 rounded-xl overflow-hidden bg-white/90 border-2 transition-all ${
                        activeImg === i ? 'border-lime-300' : 'border-transparent opacity-70 hover:opacity-100'
                      }`}
                    >
                      <img src={img} alt={`View ${i + 1}`} className="w-full h-full object-contain p-1.5" />
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* A note from Liz */}
        <section className="py-20 md:py-28">
          <div className="max-w-5xl mx-auto px-6">
            <div className="grid md:grid-cols-[280px_1fr] gap-10 md:gap-14 items-start">
              <div className="mx-auto md:mx-0 text-center md:text-left">
                <div className="w-56 h-56 md:w-64 md:h-64 rounded-2xl overflow-hidden bg-stone-100 mx-auto md:mx-0">
                  <img src={LIZ_IMG} alt="Liz Wolfe" className="w-full h-full object-cover" data-testid="liz-note-image" />
                </div>
                <p className="mt-4">
                  <span className="block font-serif text-xl text-stone-900">Liz Wolfe</span>
                  <span className="block text-xs uppercase tracking-wider text-emerald-700 mt-1">NTP, CPT &amp; Founder of Ideal Age</span>
                </p>
              </div>
              <div>
                <span className="text-xs uppercase tracking-[0.25em] text-emerald-700 font-medium">A note from Liz</span>
                <div className="mt-5 space-y-4 text-stone-700 leading-relaxed" data-testid="liz-note">
                  {LIZ_NOTE.map((para, i) => (
                    <p key={i} className={i === 0 ? 'text-lg text-stone-900' : ''}>
                      {i === 0 ? '\u201C' : ''}{para}{i === LIZ_NOTE.length - 1 ? '\u201D' : ''}
                    </p>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* The Formula */}
        <section className="py-20 md:py-28 bg-stone-50">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center max-w-2xl mx-auto mb-14">
              <span className="text-xs uppercase tracking-[0.25em] text-emerald-700 font-medium">The Formula</span>
              <h2 className="text-3xl md:text-4xl font-serif text-stone-900 mt-4 mb-5 leading-tight">
                One scoop. Four fibers. Whole-gut coverage.
              </h2>
              <p className="text-stone-600 leading-relaxed">
                Most fiber products lean on a single source. Daily Multi-Fiber blends four complementary plant fibers,
                soluble, insoluble, fermentable and resistant, so different parts of your gut microbiome get fed
                across the length of the colon.*
              </p>
            </div>
            <div className="grid sm:grid-cols-2 gap-6">
              {FIBERS.map((f) => {
                const Icon = f.icon;
                return (
                  <div key={f.title} className="group rounded-2xl bg-white border border-stone-200 p-7 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300">
                    <div className="flex items-start justify-between mb-5">
                      <div className="w-12 h-12 rounded-xl bg-emerald-50 flex items-center justify-center">
                        <Icon className="w-6 h-6 text-emerald-700" />
                      </div>
                      <span className="text-4xl font-serif text-stone-200 group-hover:text-emerald-200 transition-colors">{f.num}</span>
                    </div>
                    <span className="text-[11px] uppercase tracking-wider text-emerald-700 font-medium">{f.tag}</span>
                    <h3 className="text-xl font-semibold text-stone-900 mt-1">{f.title}</h3>
                    <p className="text-xs text-stone-400 mb-3">{f.source}</p>
                    <p className="text-sm text-stone-600 leading-relaxed">{f.body}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* How It Works — relay */}
        <section className="py-20 md:py-28">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center max-w-2xl mx-auto mb-14">
              <span className="text-xs uppercase tracking-[0.25em] text-emerald-700 font-medium">How It Works</span>
              <h2 className="text-3xl md:text-4xl font-serif text-stone-900 mt-4 mb-5 leading-tight">
                Four fibers, four speeds, one relay
              </h2>
              <p className="text-stone-600 leading-relaxed">
                A single fiber ferments in one place. These four ferment at different rates and reach different
                stretches of the colon, so beneficial bacteria get fed from the first bend all the way to the deep end.*
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {RELAY.map((r, i) => {
                const Icon = r.icon;
                return (
                  <div key={r.title} className="relative rounded-2xl border border-stone-200 p-6 bg-white">
                    <div className="w-11 h-11 rounded-full bg-emerald-700 text-white flex items-center justify-center mb-5">
                      <Icon className="w-5 h-5" />
                    </div>
                    <span className="text-[11px] uppercase tracking-wider text-emerald-700 font-medium">{r.kicker}</span>
                    <h3 className="text-base font-semibold text-stone-900 mt-1 mb-3 leading-snug">{r.title}</h3>
                    <p className="text-sm text-stone-600 leading-relaxed">{r.body}</p>
                    <span className="absolute top-6 right-6 text-xs font-mono text-stone-300">0{i + 1}</span>
                  </div>
                );
              })}
            </div>
            <div className="mt-10 rounded-2xl bg-emerald-50 border border-emerald-100 p-7 md:p-9 max-w-4xl mx-auto">
              <div className="flex items-start gap-4">
                <Sprout className="w-6 h-6 text-emerald-700 flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-semibold text-emerald-900 mb-2">The anchor: Solnul®</h4>
                  <p className="text-sm text-emerald-800/90 leading-relaxed">
                    Solnul® is the &ldquo;ambilateral&rdquo; one. It travels intact like an insoluble fiber, then ferments
                    completely like a soluble one. Because it&rsquo;s slow and low-FODMAP at its studied dose, it extends
                    coverage into the deep colon without the urgency faster fibers can cause, which is a big part of why
                    the blend feels gentle enough for every day.*
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* What You'll Feel */}
        <section className="py-20 md:py-28 bg-[#12261d] text-white">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center max-w-2xl mx-auto mb-14">
              <span className="text-xs uppercase tracking-[0.25em] text-lime-200/90 font-medium">What You&rsquo;ll Feel</span>
              <h2 className="text-3xl md:text-4xl font-serif mt-4 leading-tight">Built for everyday digestive wellness</h2>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              {FEELINGS.map((f) => {
                const Icon = f.icon;
                return (
                  <div key={f.title} className="rounded-2xl bg-white/5 border border-white/10 p-8 text-center">
                    <div className="w-14 h-14 rounded-2xl bg-lime-400/15 flex items-center justify-center mx-auto mb-5">
                      <Icon className="w-7 h-7 text-lime-200" />
                    </div>
                    <h3 className="text-lg font-semibold mb-3">{f.title}</h3>
                    <p className="text-sm text-white/70 leading-relaxed">{f.body}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* The Difference — comparison */}
        <section className="py-20 md:py-28">
          <div className="max-w-5xl mx-auto px-6">
            <div className="text-center max-w-2xl mx-auto mb-14">
              <span className="text-xs uppercase tracking-[0.25em] text-emerald-700 font-medium">The Difference</span>
              <h2 className="text-3xl md:text-4xl font-serif text-stone-900 mt-4 mb-5 leading-tight">
                How Daily Multi-Fiber compares
              </h2>
              <p className="text-stone-600 leading-relaxed">
                Not all fiber is created equal. Here&rsquo;s how a doctor-formulated, 4-fiber blend stacks up against the
                fiber products you&rsquo;ll find on most shelves.
              </p>
            </div>
            <div className="overflow-x-auto rounded-2xl border border-stone-200">
              <table className="w-full text-sm min-w-[640px]" data-testid="fiber-compare-table">
                <thead>
                  <tr className="border-b border-stone-200">
                    <th className="text-left font-medium text-stone-500 p-4 w-1/3">&nbsp;</th>
                    <th className="p-4 text-center bg-emerald-50 text-emerald-800 font-semibold">Daily Multi-Fiber</th>
                    <th className="p-4 text-center text-stone-500 font-medium">Typical Fiber Powders</th>
                    <th className="p-4 text-center text-stone-500 font-medium">Fiber Gummies</th>
                  </tr>
                </thead>
                <tbody>
                  {COMPARE.map((row, i) => (
                    <tr key={row.label} className={i % 2 ? 'bg-stone-50/60' : ''}>
                      <td className="p-4 text-stone-700 font-medium">{row.label}</td>
                      <td className="p-4 text-center bg-emerald-50/40"><CompareCell value={row.a} strong /></td>
                      <td className="p-4 text-center"><CompareCell value={row.b} /></td>
                      <td className="p-4 text-center"><CompareCell value={row.c} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-stone-400 mt-4 text-center max-w-2xl mx-auto">
              Comparison reflects formulations commonly found in each category, not any specific brand. Attributes vary
              from product to product. Always check the label.
            </p>
          </div>
        </section>

        {/* The Science */}
        <section className="py-20 md:py-28 bg-stone-50">
          <div className="max-w-6xl mx-auto px-6">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <span className="text-xs uppercase tracking-[0.25em] text-emerald-700 font-medium">The Science</span>
                <h2 className="text-3xl md:text-4xl font-serif text-stone-900 mt-4 mb-5 leading-tight">
                  Backed by a placebo-controlled human trial
                </h2>
                <p className="text-stone-600 leading-relaxed mb-8">
                  Solnul® is one of the few resistant starches studied in a randomized, double-blind, placebo-controlled
                  clinical trial in healthy adults. Over four weeks, a low daily dose was shown to support healthy levels
                  of beneficial gut bacteria, and better stool form, compared with placebo.*
                </p>
                <div className="flex gap-8">
                  <div>
                    <div className="text-4xl font-serif text-emerald-700">4 wks</div>
                    <div className="text-xs uppercase tracking-wider text-stone-500 mt-1">Duration of trial</div>
                  </div>
                  <div>
                    <div className="text-4xl font-serif text-emerald-700">75</div>
                    <div className="text-xs uppercase tracking-wider text-stone-500 mt-1">Healthy adults studied</div>
                  </div>
                </div>
              </div>
              <div className="space-y-5">
                {[
                  { icon: FlaskConical, title: 'Bifidobacterium', body: 'A cornerstone of a healthy gut. In the trial, Solnul® supported significant increases versus placebo.*' },
                  { icon: Beaker, title: 'Akkermansia', body: 'Associated with the protective lining of the gut. The increases observed were noted by researchers as novel.*' }
                ].map((c) => {
                  const Icon = c.icon;
                  return (
                    <div key={c.title} className="flex items-start gap-4 rounded-2xl bg-white border border-stone-200 p-6">
                      <div className="w-11 h-11 rounded-xl bg-emerald-50 flex items-center justify-center flex-shrink-0">
                        <Icon className="w-5 h-5 text-emerald-700" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-stone-900 mb-1">{c.title}</h3>
                        <p className="text-sm text-stone-600 leading-relaxed">{c.body}</p>
                      </div>
                    </div>
                  );
                })}
                <p className="text-xs text-stone-400 leading-relaxed">
                  Clinical research refers to the Solnul® resistant potato starch. Daily Multi-Fiber delivers Solnul® as
                  part of its 10g premium fiber blend.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Supplement facts + How to use */}
        <section className="py-20 md:py-28">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center max-w-2xl mx-auto mb-14">
              <span className="text-xs uppercase tracking-[0.25em] text-emerald-700 font-medium">Label &amp; Directions</span>
              <h2 className="text-3xl md:text-4xl font-serif text-stone-900 mt-4 leading-tight">
                Simple to read. Simpler to use.
              </h2>
            </div>
            <div className="grid lg:grid-cols-2 gap-8">
              {/* Supplement facts */}
              <div>
                <div className="rounded-2xl border-2 border-stone-900 p-6" data-testid="fiber-supplement-facts">
                  <h3 className="text-2xl font-bold text-stone-900 tracking-tight">Supplement Facts</h3>
                  {/* thick rule after title */}
                  <div className="border-t-4 border-stone-900 mt-1" />
                  <p className="text-xs text-stone-600 mt-1">Serving Size: 1 rounded scoop (approx 14.9g)</p>
                  <p className="text-xs text-stone-600">Servings Per Container: 30 servings</p>

                  {/* thick rule after Servings Per Container */}
                  <div className="border-t-4 border-stone-900 mt-2" />

                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-stone-400 text-xs text-stone-600">
                        <th className="text-left py-1 font-normal">&nbsp;</th>
                        <th className="text-right py-1 font-semibold">Amount Per Serving</th>
                        <th className="text-right py-1 font-semibold">% Daily Value**</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b border-stone-300">
                        <td className="py-1.5 text-stone-800">Calories</td>
                        <td className="py-1.5 text-right text-stone-800">25</td>
                        <td className="py-1.5 text-right text-stone-800" />
                      </tr>
                      <tr className="border-b border-stone-300">
                        <td className="py-1.5 text-stone-800">Total Carbohydrate</td>
                        <td className="py-1.5 text-right text-stone-800">12 g</td>
                        <td className="py-1.5 text-right text-stone-800">4%</td>
                      </tr>
                      <tr className="border-b border-stone-300">
                        <td className="py-1.5 pl-6 text-stone-800">Dietary Fiber</td>
                        <td className="py-1.5 text-right text-stone-800">10 g</td>
                        <td className="py-1.5 text-right text-stone-800">36%</td>
                      </tr>
                      <tr className="border-b border-stone-300">
                        <td className="py-1.5 text-stone-800">Calcium</td>
                        <td className="py-1.5 text-right text-stone-800">35 mg</td>
                        <td className="py-1.5 text-right text-stone-800">2%</td>
                      </tr>
                      {/* thick rule above Premium Fiber Blend */}
                      <tr className="border-t-4 border-stone-900">
                        <td className="py-1.5 font-bold text-stone-900">Premium Fiber Blend</td>
                        <td className="py-1.5 text-right font-bold text-stone-900">10 g</td>
                        <td className="py-1.5 text-right font-bold text-stone-900">&dagger;</td>
                      </tr>
                      {/* ingredient list — thick rule below (below Chicory Root) */}
                      <tr className="border-b-4 border-stone-900">
                        <td colSpan={3} className="pb-2 pl-6 text-xs text-stone-600 leading-relaxed">
                          Organic Acacia gum, Solnul® (Resistant Potato Starch), Short Chain Fructooligosaccharides
                          (from Sugar Beet) and Inulin (from Chicory Root)
                        </td>
                      </tr>
                    </tbody>
                  </table>

                  <p className="text-xs text-stone-500 mt-3 leading-relaxed">
                    &dagger; Daily Value not established.<br />
                    **Percent Daily Values are based on a 2,000 calorie diet.
                  </p>
                </div>

                {/* Outside the Supplement Facts panel */}
                <p className="text-sm text-stone-700 mt-4">
                  <span className="font-semibold text-stone-900">Other Ingredients:</span> None.
                </p>
                <p className="text-[11px] text-stone-400 mt-3">
                  Solnul® is a registered trademark of MSP Starch Products Inc.
                </p>
              </div>

              {/* How to use */}
              <div className="rounded-2xl bg-stone-50 border border-stone-200 p-8">
                <h3 className="text-lg font-semibold text-stone-900 mb-6">How to use it</h3>
                <ol className="space-y-5">
                  {HOW_TO.map((step, i) => (
                    <li key={i} className="flex items-start gap-4">
                      <span className="w-8 h-8 rounded-full bg-emerald-700 text-white text-sm font-semibold flex items-center justify-center flex-shrink-0">
                        {i + 1}
                      </span>
                      <p className="text-sm text-stone-700 leading-relaxed pt-1">{step}</p>
                    </li>
                  ))}
                </ol>
                <div className="mt-8 rounded-xl bg-white border border-stone-200 p-5">
                  <p className="text-sm text-stone-600 italic leading-relaxed">
                    &ldquo;Best taken 15 minutes before your first meal for maximum effect. This blend supports healthy,
                    steady digestion thanks to fiber that acts like both soluble and insoluble fibers.&rdquo;
                  </p>
                  <p className="text-xs text-stone-500 mt-3 font-medium">Shanti Ramjeet, RPH · Pharmacist-Formulator, MD Logic Health®</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Clean by Design */}
        <section className="py-20 md:py-28 bg-stone-50">
          <div className="max-w-5xl mx-auto px-6">
            <div className="text-center max-w-2xl mx-auto mb-12">
              <span className="text-xs uppercase tracking-[0.25em] text-emerald-700 font-medium">Clean by Design</span>
              <h2 className="text-3xl md:text-4xl font-serif text-stone-900 mt-4 mb-5 leading-tight">
                What you won&rsquo;t find inside
              </h2>
              <p className="text-stone-600 leading-relaxed">
                An additive-free formula with nothing to hide. Lab-tested for contaminants, including heavy metals and mold.
              </p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
              {FREE_FROM.map((item) => (
                <div key={item} className="flex items-center gap-2 rounded-xl bg-white border border-stone-200 px-4 py-3">
                  <X className="w-4 h-4 text-rose-400 flex-shrink-0" />
                  <span className="text-xs text-stone-600">{item.replace('No ', '')}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-20 md:py-28">
          <div className="max-w-3xl mx-auto px-6">
            <div className="text-center mb-12">
              <span className="text-xs uppercase tracking-[0.25em] text-emerald-700 font-medium">Good to Know</span>
              <h2 className="text-3xl md:text-4xl font-serif text-stone-900 mt-4 leading-tight">Your questions, answered</h2>
            </div>
            <Accordion type="single" collapsible className="w-full" data-testid="fiber-faq">
              {FAQS.map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`} data-testid={`fiber-faq-item-${i}`}>
                  <AccordionTrigger className="text-left text-stone-900 hover:no-underline">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-stone-600 leading-relaxed">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-20 md:py-28 bg-gradient-to-b from-[#173026] to-[#12261d] text-white">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-3xl md:text-5xl font-serif mb-5 leading-tight">
              Make fiber the easiest part of your day.
            </h2>
            <p className="text-white/70 max-w-xl mx-auto mb-10 leading-relaxed">
              One clean scoop of four plant fibers to feed your gut, support regularity, and keep you satisfied.*
            </p>
            <div className="max-w-md mx-auto">
              <BuyBox
                price={price} image={images[0]} quantity={quantity} setQuantity={setQuantity}
                onAdd={handleAddToCart} adding={adding} available={available} variant="dark" centered
              />
            </div>
            <p className="text-[11px] text-white/40 mt-8 max-w-2xl mx-auto leading-relaxed">
              *These statements have not been evaluated by the Food and Drug Administration. This product is not intended
              to diagnose, treat, cure, or prevent any disease.
            </p>
          </div>
        </section>
      </main>

      <Footer />

      <CartDrawer
        isOpen={cartOpen}
        onClose={() => setCartOpen(false)}
        cart={cart}
        onUpdateQuantity={handleUpdateQuantity}
        onRemoveItem={handleRemoveItem}
        onCheckout={handleCheckout}
        loading={cartLoading}
      />
    </div>
  );
};

export default DailyMultiFiber;
